// frontend/lib/diagnostics/probes.js
//
// Self-contained connectivity probes for the Diagnostic page. Each probe is
// an async function that returns a structured result object. They are designed
// to run independently — a failure in one probe never prevents the others from
// running, and each probe cleans up after itself.
//
// Probes map 1:1 to the DiagnosticReport schema fields on the backend:
//   collectClientInfo()      → clientInfo
//   probeHttpEcho()          → httpProbe + serverHealth
//   probeStreaming()         → streamProbe
//   probeAuth()              → authProbe
//   probeIsolatedSocket()    → socketProbe
//   snapshotSingletonSocket() → singletonSnapshot
//
// The SW probe (swProbe) lives in a separate file because it's web-only and
// uses browser APIs that don't exist on native.

import { Platform } from 'react-native';
import { io } from 'socket.io-client';
import { API_HOST, SOCKET_HOST, BUILD_ID } from '@/constants/config';

// ---------------------------------------------------------------------------
// 1. Client info — navigator.onLine + Network Information API + device context
// ---------------------------------------------------------------------------
export function collectClientInfo() {
    const isWeb = Platform.OS === 'web';
    const nav = isWeb ? window?.navigator : null;
    const conn = nav?.connection || nav?.mozConnection || nav?.webkitConnection || null;

    // Parse user-agent into browser + OS labels (web only; native uses Platform)
    let browser = null;
    let os = null;
    const ua = nav?.userAgent || '';
    if (ua) {
        if (/Edg\//.test(ua)) browser = 'Edge';
        else if (/Chrome\//.test(ua) && !/OPR\//.test(ua)) browser = 'Chrome';
        else if (/Firefox\//.test(ua)) browser = 'Firefox';
        else if (/Safari\//.test(ua)) browser = 'Safari';
        else browser = ua.slice(0, 60);

        if (/Windows/.test(ua)) os = 'Windows';
        else if (/Mac OS X/.test(ua)) os = 'macOS';
        else if (/Android/.test(ua)) os = 'Android';
        else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
        else if (/Linux/.test(ua)) os = 'Linux';
    }

    if (Platform.OS === 'ios') os = os || 'iOS';
    if (Platform.OS === 'android') os = os || 'Android';
    if (Platform.OS === 'macos') os = os || 'macOS';

    return {
        platform: Platform.OS,
        os,
        browser,
        userAgent: ua || null,
        screenW: isWeb && window?.screen ? window.screen.width : null,
        screenH: isWeb && window?.screen ? window.screen.height : null,
        viewportW: isWeb && window ? window.innerWidth : null,
        viewportH: isWeb && window ? window.innerHeight : null,
        timezone: isWeb ? (Intl?.DateTimeFormat()?.resolvedOptions?.()?.timeZone || null) : null,
        locale: isWeb ? (nav?.language || nav?.languages?.[0] || null) : null,
        online: nav ? nav.onLine : null,
        networkType: conn?.effectiveType || null,
        downlink: conn?.downlink ?? null,
        rtt: conn?.rtt ?? null,
        saveData: conn?.saveData ?? null,
    };
}

// ---------------------------------------------------------------------------
// 2. HTTP echo — times GET /json/diagnostics/echo
// ---------------------------------------------------------------------------
export async function probeHttpEcho() {
    const start = Date.now();
    try {
        const res = await fetch(`${API_HOST}/json/diagnostics/echo`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });
        const durationMs = Date.now() - start;
        const data = await res.json();
        const serverTimeSkewMs = data.serverTime
            ? Date.now() - new Date(data.serverTime).getTime()
            : null;
        return {
            ok: res.ok && data.ok === true,
            status: res.status,
            durationMs,
            serverTimeSkewMs,
            serverData: data,
            // Cloudflare edge fields echoed back by the server. These are
            // also captured server-side on the subsequent POST (authoritative),
            // but mirroring them on the echo lets the user see their real
            // IPv6 vs the synthetic pseudo-IPv4 bogon immediately.
            cf: data.cf || null,
            error: null,
        };
    } catch (err) {
        return {
            ok: false,
            status: null,
            durationMs: Date.now() - start,
            serverTimeSkewMs: null,
            serverData: null,
            cf: null,
            error: err?.message || String(err),
        };
    }
}

// ---------------------------------------------------------------------------
// 2b. True streaming probe — GET /json/diagnostics/stream
//     Consumes the SSE stream chunk-by-chunk, measuring first-byte time and
//     the gap between chunks. If firstByte arrives seconds late but chunks
//     then arrive in a burst, the path is buffering (corporate VPN, broken
//     proxy, misconfigured Argo). This is the same failure mode that breaks
//     voice-mode SSE for the affected user.
// ---------------------------------------------------------------------------
export async function probeStreaming(timeoutMs = 15000) {
    const CHUNKS_EXPECTED = 5;
    const GAP_MS = 300; // must match routes/diagnosticRoutes.js
    const start = Date.now();
    let firstByteMs = null;
    let chunksReceived = 0;
    const chunkTimes = []; // deltas between consecutive data: events
    let lastChunkAt = null;
    let didDone = false;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const res = await fetch(`${API_HOST}/json/diagnostics/stream`, {
            credentials: 'include',
            signal: controller.signal,
            headers: { Accept: 'text/event-stream' },
        });

        if (!res.ok || !res.body) {
            clearTimeout(timeout);
            return {
                ok: false,
                status: res.status,
                chunksReceived: 0,
                chunksExpected: CHUNKS_EXPECTED,
                firstByteMs: null,
                totalMs: Date.now() - start,
                gapAvgMs: null,
                buffered: null,
                error: !res.body ? 'no_stream_body' : null,
            };
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Read loop. Each `data: ...\n\n` is one SSE event. We count chunks
        // and measure inter-chunk gaps; [DONE] ends the stream.
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (firstByteMs === null) firstByteMs = Date.now() - start;
            buffer += decoder.decode(value, { stream: true });

            // SSE event terminator is a blank line. Pull complete events out,
            // keep the trailing partial in buffer.
            const events = buffer.split('\n\n');
            buffer = events.pop() || '';
            for (const evt of events) {
                const line = evt.split('\n').find((l) => l.startsWith('data:'));
                if (!line) continue;
                const payload = line.slice(5).trim();
                if (payload === '[DONE]') { didDone = true; break; }
                chunksReceived++;
                const now = Date.now();
                if (lastChunkAt !== null) chunkTimes.push(now - lastChunkAt);
                lastChunkAt = now;
            }
            if (didDone) break;
        }

        clearTimeout(timeout);
        const totalMs = Date.now() - start;
        const gapAvgMs = chunkTimes.length
            ? Math.round(chunkTimes.reduce((a, b) => a + b, 0) / chunkTimes.length)
            : null;

        // Buffering heuristic: server emits chunks GAP_MS apart. If we got the
        // first byte much later than GAP_MS after request start (relative to
        // the gap we actually measured), the path held the response back.
        // Compare firstByte to gap to be robust against slow networks where
        // everything is uniformly delayed.
        const buffered = firstByteMs !== null && gapAvgMs !== null
            ? firstByteMs > Math.max(GAP_MS * 3, (gapAvgMs * 2) + 500)
            : null;

        return {
            ok: didDone && chunksReceived >= CHUNKS_EXPECTED,
            status: res.status,
            chunksReceived,
            chunksExpected: CHUNKS_EXPECTED,
            firstByteMs,
            totalMs,
            gapAvgMs,
            buffered,
            error: null,
        };
    } catch (err) {
        clearTimeout(timeout);
        const aborted = err?.name === 'AbortError';
        return {
            ok: false,
            status: null,
            chunksReceived,
            chunksExpected: CHUNKS_EXPECTED,
            firstByteMs,
            totalMs: Date.now() - start,
            gapAvgMs: null,
            buffered: null,
            error: aborted ? 'timeout' : (err?.message || String(err)),
        };
    }
}

// ---------------------------------------------------------------------------
// 3. Auth probe — token presence, expiry, refresh, and a guarded GET
// ---------------------------------------------------------------------------
export async function probeAuth() {
    // Lazy require to avoid circular deps at module load time.
    const { useAuthStore } = require('@/store');

    const state = useAuthStore.getState();
    const token = state.token;
    const tokenPresent = !!token;

    // Decode JWT exp (without verifying — we're just reporting, not trusting).
    let tokenExpiredAt = null;
    if (token && token.split('.').length === 3) {
        try {
            // atob is web-only; on native use the bundled base64 polyfill.
            const decode = typeof atob === 'function' ? atob : (s) => {
                const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
                let str = s.replace(/=+$/, '');
                let output = '';
                for (let bc = 0, i = 0; i < str.length; i++) {
                    const c = chars.indexOf(str[i]);
                    if (c < 0) continue;
                    bc = (bc % 4) ? bc * 64 + c : c;
                    if (bc++ % 4) output += String.fromCharCode(255 & bc >> (-2 * bc & 6));
                }
                return output;
            };
            const payload = JSON.parse(decode(token.split('.')[1]));
            if (payload.exp) tokenExpiredAt = new Date(payload.exp * 1000).toISOString();
        } catch (_) { /* malformed JWT — leave null */ }
    }

    // Try a token refresh — reports whether the refresh path works.
    let refreshOk = null;
    try {
        if (typeof state.getValidToken === 'function') {
            const fresh = await state.getValidToken({ reconnectSocket: false });
            refreshOk = !!fresh;
        }
    } catch (_err) {
        refreshOk = false;
    }

    // Guarded GET — hit a lightweight authenticated endpoint to see if the
    // server accepts our token. /json/diagnostics/echo is unauthenticated,
    // so use the /json/user/me pattern (any authenticated GET works).
    let guardedGetStatus = null;
    let guardedError = null;
    try {
        const res = await fetch(`${API_HOST}/json/user/me`, {
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        guardedGetStatus = res.status;
    } catch (err) {
        guardedError = err?.message || String(err);
    }

    return {
        tokenPresent,
        tokenExpiredAt,
        refreshOk,
        guardedGetStatus,
        error: guardedError,
    };
}

// ---------------------------------------------------------------------------
// 4. Isolated socket probe — a FRESH io() connection, separate from the app's
//    singleton socketManager. Measures handshake time, transport achieved
//    (polling vs websocket), connect errors, upgrade errors, and a ping/pong
//    round-trip. Fully cleaned up on completion.
// ---------------------------------------------------------------------------
export async function probeIsolatedSocket(timeoutMs = 15000) {
    return new Promise((resolve) => {
        const start = Date.now();
        let settled = false;
        let handshakeMs = null;
        // transport is updated THREE times during the probe:
        //   1. at connect (always 'polling' — the upgrade hasn't happened yet)
        //   2. on 'upgrade' event (becomes 'websocket')
        //   3. re-read from engine at finish() time as a belt-and-suspenders
        // catch in case the upgrade fired between events.
        let transport = null;
        let transportUpgraded = false;
        let upgradeError = null;
        let connectError = null;
        let socket = null;
        let pongTimeout = null;
        let upgradeWindowTimer = null;

        const finish = (extra = {}) => {
            if (settled) return;
            settled = true;
            // Re-read the live transport at finish time — the upgrade may
            // have fired after our last explicit update.
            const liveTransport = socket?.io?.engine?.transport?.name;
            const finalTransport = liveTransport || transport;
            cleanup();
            resolve({
                connected: extra.connected ?? false,
                transport: finalTransport,
                transportUpgraded,
                upgradeError,
                handshakeMs,
                connectError,
                pingPongMs: extra.pingPongMs ?? null,
            });
        };

        const timeout = setTimeout(() => {
            finish({ connected: false });
        }, timeoutMs);

        const cleanup = () => {
            clearTimeout(timeout);
            clearTimeout(pongTimeout);
            clearTimeout(upgradeWindowTimer);
            if (socket) {
                try {
                    socket.removeAllListeners();
                    socket.disconnect();
                } catch (_) { /* already gone */ }
            }
        };

        try {
            // Auth callback mirrors the app's singleton socketManager — server
            // disconnects sockets without a valid token (appSocket.js:135-148).
            const { useAuthStore } = require('@/store');

            socket = io(SOCKET_HOST, {
                transports: ['polling', 'websocket'],
                tryAllTransports: true,
                reconnection: false,    // one-shot probe — no auto-retry
                timeout: timeoutMs,
                forceNew: true,
                withCredentials: true,
                auth: (cb) => {
                    useAuthStore.getState().getValidToken({ reconnectSocket: false })
                        .then((freshToken) => {
                            cb({ token: freshToken, platform: Platform.OS });
                        })
                        .catch(() => {
                            cb({ token: null, platform: Platform.OS });
                        });
                },
            });

            // ----- Transport upgrade tracking -----
            //
            // The connect event ALWAYS fires on polling (the WS upgrade is a
            // parallel attempt that completes AFTER the polling handshake).
            // Without these listeners the probe reported 'polling' forever,
            // even when CF analytics showed a 101 Switching Protocols seconds
            // later. The 'upgrade' event fires once the WS transport is
            // verified and Engine.IO has switched to it.
            socket.on('upgrade', () => {
                transportUpgraded = true;
                transport = socket?.io?.engine?.transport?.name || 'websocket';
            });

            // Upgrade failure does NOT fail the probe — the socket is still
            // functional on polling. We record the error so the UI can show
            // "stuck on polling (upgrade failed: ...)" instead of just "polling".
            socket.on('upgradeError', (err) => {
                upgradeError = err?.message || String(err);
            });

            socket.on('connect', () => {
                handshakeMs = Date.now() - start;
                // Initial transport — will be 'polling'. The upgrade handler
                // above will overwrite this if/when WS upgrade succeeds.
                transport = socket?.io?.engine?.transport?.name || null;

                // App-level ping/pong. The server's ping handler
                // (appSocket.js:207) reads userId from the payload and emits
                // pong to the user room this socket auto-joined at connect
                // (appSocket.js:187, emitPong → _emitEvent(io, userId, ...)).
                // Without userId the emit goes to room `undefined` and this
                // probe never sees the pong — which is exactly why
                // pingPongMs came back null previously.
                let userId = null;
                try {
                    userId = useAuthStore.getState()?.user?.id || null;
                } catch (_) { /* best-effort */ }

                const pingStart = Date.now();
                let pongReceived = false;

                socket.on('pong', () => {
                    if (pongReceived) return;
                    pongReceived = true;
                    // Don't finish immediately — give the upgrade a brief
                    // window to complete so the recorded transport reflects
                    // the real final state. If the upgrade already fired,
                    // the 'upgrade' listener has already updated `transport`.
                    // 500ms is enough for any in-flight upgrade to settle
                    // without materially slowing the probe.
                    upgradeWindowTimer = setTimeout(() => {
                        finish({ connected: true, pingPongMs: Date.now() - pingStart });
                    }, 500);
                });

                socket.emit('ping', { userId, t: pingStart });

                pongTimeout = setTimeout(() => {
                    // No pong in 5s — still finish so we capture transport
                    // state. pingPongMs stays null and the UI can flag it.
                    finish({ connected: true, pingPongMs: null });
                }, 5000);
            });

            socket.on('connect_error', (err) => {
                connectError = err?.message || String(err);
                finish({ connected: false });
            });
        } catch (err) {
            connectError = err?.message || String(err);
            finish({ connected: false });
        }
    });
}

// ---------------------------------------------------------------------------
// 5. Singleton socket snapshot — point-in-time state of the app's main
//    socketManager. Note: socket.connected can diverge from the manager's
//    isConnected flag (documented in socket.js:282-289 re: iOS suspension).
// ---------------------------------------------------------------------------
export function snapshotSingletonSocket() {
    // Lazy require to avoid circular deps.
    const socketManager = require('@/lib/socket').default;

    try {
        const socket = socketManager.socket;

        // The singleton socketManager is initialized inside (app)/_layout.jsx.
        // When the diagnostics page is opened as a standalone entry route
        // (outside the (app) group), the socket may never have been created.
        // That's NOT a failure — it just means the singleton snapshot is only
        // meaningful when the page is opened from within the running app.
        if (!socket) {
            return {
                initialized: false,
                isSocketConnected: false,
                rawSocketConnected: false,
                note: 'App socket not initialized — this page is outside the main app. The isolated socket test above is the definitive check.',
            };
        }

        return {
            initialized: true,
            isSocketConnected: socketManager.isSocketConnected(),
            rawSocketConnected: socket?.connected ?? false,
            isRateLimited: socketManager.isRateLimited(),
            rateLimitedUntilMs: socketManager._rateLimitedUntilMs || null,
            transport: socket?.io?.engine?.transport?.name || null,
            reconnectAttempts: socket?.io?._reconnectionAttempts || 0,
        };
    } catch (err) {
        return {
            initialized: false,
            error: err?.message || String(err),
        };
    }
}

// ---------------------------------------------------------------------------
// 6. Timeline capture — attaches transient listeners to the singleton socket
//    for the duration the diagnostic page is open. Returns { events, stop }.
//    Call stop() on unmount to remove listeners.
// ---------------------------------------------------------------------------
export function startTimelineCapture() {
    const socketManager = require('@/lib/socket').default;
    const events = [];
    const handlers = {};

    const addEvent = (type) => (...args) => {
        const detail = args.length === 0
            ? ''
            : (typeof args[0] === 'string'
                ? args[0]
                : (() => { try { return JSON.stringify(args[0]).slice(0, 200); } catch { return String(args[0]).slice(0, 200); } })());
        events.push({ t: new Date().toISOString(), type, detail });
    };

    const eventTypes = ['connect', 'disconnect', 'connect_error', 'reconnect_attempt', 'reconnect_error', 'reconnect_failed'];
    try {
        const socket = socketManager.socket;
        if (socket) {
            for (const type of eventTypes) {
                handlers[type] = addEvent(type);
                socket.on(type, handlers[type]);
            }
        }
    } catch (_) { /* socket not initialized yet */ }

    const stop = () => {
        try {
            const socket = socketManager.socket;
            if (socket) {
                for (const [type, handler] of Object.entries(handlers)) {
                    socket.off(type, handler);
                }
            }
        } catch (_) { /* already cleaned up */ }
    };

    return { events, stop };
}

// ---------------------------------------------------------------------------
// Build IDs — collected from the running bundle. The SW probe (separate file)
// adds the origin and SW build IDs, and the versionSkew flag.
// ---------------------------------------------------------------------------
export function getBundleBuildId() {
    return BUILD_ID;
}
